// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Framework lifecycle statuses under which a framework naming a persona does not
 * make the persona current: retired (`legacy`, `superseded`) or not yet law
 * (`draft`). One set for the server's relevance, the Persona Paths screen and
 * `census:persona-frameworks` (fix-sprint-20261017-2115 L7: the server held
 * {legacy, draft} while the other two held {legacy, superseded, draft}, so a
 * superseded framework would have counted on the server and been folded away
 * on the screen).
 */
export const PERSONA_RETIRED_FRAMEWORK_STATUSES = new Set([
    'legacy',
    'superseded',
    'draft',
]);
/**
 * Which personas a framework is relevant to, read from what its pack declares.
 *
 * Replaces `FRAMEWORK_PERSONA_RELEVANCE` (2026-09-25), a constant that carried
 * the authored matrix into `src/shared` as one framework-code literal per row.
 * The pack now declares `persona_relevance` on the framework record, the
 * installer persists it with the record, and this reads it back, so no
 * framework is named in application code (R-46).
 *
 * `primary` comes first, then each `supporting` persona not already listed. A
 * framework that declares nothing takes its parent's declaration — a national
 * transposition inherits its directive's personas — and one with neither
 * returns `[]` and is still offered: a framework a tenant has installed is one
 * it is subject to, and hiding it because nobody mapped it would make the list
 * quietly wrong.
 *
 * A declaration that is not the declared shape reads as absent rather than
 * throwing: the installed record is data, and the caller is a list.
 */
export function resolvePersonaRelevance(framework, parent) {
    const entry = resolvePersonaRelevanceDeclaration(framework, parent);
    if (!entry)
        return [];
    return [...entry.primary, ...entry.supporting];
}
/**
 * Sprint 63.28.0 (A5) — the same resolution as `resolvePersonaRelevance`, with
 * the primary/supporting split kept, because Persona Paths counts the two
 * separately and the framework list on a persona's detail says which one the
 * persona is. `supporting` excludes every persona already in `primary`, so a
 * persona is counted once per framework. `undefined` when neither record
 * declares the shape.
 */
export function resolvePersonaRelevanceDeclaration(framework, parent) {
    // Sprint 63.35.0 (U13) — a record carrying the no-addressee marker has
    // declared: it resolves to no persona and does not inherit its parent's.
    if (readNoAddressee(framework?.persona_relevance))
        return undefined;
    const entry = readDeclaration(framework?.persona_relevance) ?? readDeclaration(parent?.persona_relevance);
    if (!entry)
        return undefined;
    return {
        primary: [...entry.primary],
        supporting: entry.supporting.filter((p) => !entry.primary.includes(p)),
        ...(entry.basis ? { basis: { ...entry.basis } } : {}),
    };
}
/**
 * Sprint 63.35.0 (U13, decision 3) — whether a record declares itself, either
 * with a persona list or with the no-addressee marker. A marked record is
 * declared: the inheritance walks stop at it. The runtime index, the census
 * and the persona matrix all ask this one question.
 */
export function declaresPersonaRelevance(framework) {
    const value = framework?.persona_relevance;
    if (readNoAddressee(value))
        return true;
    const entry = readDeclaration(value);
    return !!entry && entry.primary.length + entry.supporting.length > 0;
}
/**
 * Sprint 63.35.0 (U13) — the marker's citation and reading, or `undefined`
 * when the record does not carry it.
 */
export function readNoOrganisationalAddressee(framework) {
    return readNoAddressee(framework?.persona_relevance);
}
/**
 * Sprint 63.35.0 (U1, decision 2) — one persona's basis on a framework, and
 * the code of the record it was read from. A record that declares nothing
 * inherits its parent's personas WITH the parent's citations, and
 * `inherited_from` says so: Persona Paths shows "attached through <parent>".
 * `undefined` when the persona is not attached, or when the declaration is an
 * older installed one with no basis for it.
 */
export function resolvePersonaBasis(personaId, framework, parent) {
    if (readNoAddressee(framework?.persona_relevance))
        return undefined;
    const own = readDeclaration(framework?.persona_relevance);
    const from = own ? framework : parent;
    const entry = own ?? readDeclaration(parent?.persona_relevance);
    if (!entry || !(entry.primary.includes(personaId) || entry.supporting.includes(personaId))) {
        return undefined;
    }
    const basis = entry.basis?.[personaId];
    if (!basis)
        return undefined;
    const parentCode = typeof from?.framework_code === 'string' ? from.framework_code : undefined;
    return own || !parentCode ? { ...basis } : { ...basis, inherited_from: parentCode };
}
/**
 * Sprint 63.28.0 (A5) — the one ranking of personas by how much the
 * organisation's installed frameworks name them: installed primary count
 * descending, then installed supporting count descending, then jurisdiction in
 * the order VN, EU, UK, GL, then `persona_id` ascending, so the order is total
 * and stable. Persona Paths sorts by it, and detection's role branch breaks a
 * tie between two available role candidates the same way.
 *
 * The jurisdiction step (gate E12.5 round 1, P3) makes an equal rank fall the
 * way `resolveRolePersona` falls — in the FIXED order VN, EU, UK, then the
 * ungated global personas last, the same order for every tenant (it does not
 * read which market a tenant is in) — rather than by the alphabet, which put
 * `P-EU-` before `P-VN-` and `P-GL-` before both. The jurisdiction is the
 * persona ID's namespace (`P-<J>-NN`), which is how the registry derives its
 * own `jurisdiction`; an ID outside the four sorts after them.
 */
export function comparePersonaRelevance(a, b) {
    return (b.relevance.installed_primary - a.relevance.installed_primary ||
        b.relevance.installed_supporting - a.relevance.installed_supporting ||
        jurisdictionRank(a.persona_id) - jurisdictionRank(b.persona_id) ||
        (a.persona_id < b.persona_id ? -1 : a.persona_id > b.persona_id ? 1 : 0));
}
/** The equal-rank order of the role branch: fixed VN, EU, UK, GL for every tenant (gate E12.5 round 2). */
const JURISDICTION_ORDER = ['VN', 'EU', 'UK', 'GL'];
function jurisdictionRank(personaId) {
    const ns = /^P-([A-Z]+)-/.exec(personaId)?.[1];
    const i = JURISDICTION_ORDER.indexOf(ns);
    return i === -1 ? JURISDICTION_ORDER.length : i;
}
function readDeclaration(value) {
    if (!value || typeof value !== 'object')
        return undefined;
    const { primary, supporting, basis } = value;
    if (!isStringArray(primary) || !isStringArray(supporting))
        return undefined;
    const bases = readBasisMap(basis);
    return bases ? { primary, supporting, basis: bases } : { primary, supporting };
}
function readBasisMap(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return undefined;
    const out = {};
    for (const [id, raw] of Object.entries(value)) {
        const basis = readBasis(raw);
        if (basis)
            out[id] = basis;
    }
    return out;
}
function readBasis(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return undefined;
    const { citation, reading, source, second_read } = value;
    if (!citation || typeof citation !== 'object')
        return undefined;
    const { framework_code, article } = citation;
    if (typeof framework_code !== 'string' || typeof article !== 'string')
        return undefined;
    if (typeof reading !== 'string' || typeof source !== 'string')
        return undefined;
    if (second_read !== 'agreed' && second_read !== 'pending')
        return undefined;
    return { citation: { framework_code, article }, reading, source, second_read };
}
function readNoAddressee(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return undefined;
    return readBasis(value.no_organisational_addressee);
}
const isStringArray = (v) => Array.isArray(v) && v.every((x) => typeof x === 'string');
//# sourceMappingURL=persona-relevance.js.map